import { useQuery } from '@tanstack/react-query'
import type { ActionStatus } from '../lib/types'
import { ACTION_STATUS_LABEL, ACTION_STATUS_OPTIONS } from '../lib/format'
import { clientsApi } from '../api/clients'

/**
 * Barra de filtros de Ações: Cliente + Status (padrão Pendente) + duas datas por
 * semana (Seg–Dom): a do RELATÓRIO e a do REVER.
 */
export type ActionsStatusFilter = ActionStatus | 'all'

export function ActionsFilters({
  status,
  week,
  reviewWeek,
  clientId,
  onStatus,
  onWeek,
  onReviewWeek,
  onClientId,
  onReset,
  isDefault,
}: {
  status: ActionsStatusFilter
  week: string
  reviewWeek: string
  clientId: string
  onStatus: (s: ActionsStatusFilter) => void
  onWeek: (w: string) => void
  onReviewWeek: (w: string) => void
  onClientId: (id: string) => void
  /** Volta os filtros ao padrão. Quando ausente, o botão "Limpar filtros" não é exibido. */
  onReset?: () => void
  /** true quando os filtros estão no padrão — deixa o botão "Limpar filtros" inativo. */
  isDefault?: boolean
}) {
  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.list(),
  })
  const clients = clientsData?.clients ?? []

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-3">
      <div>
        <label className="mb-1 block text-xs font-semibold text-slate-500">Cliente</label>
        <select
          value={clientId}
          onChange={(e) => onClientId(e.target.value)}
          className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
        >
          <option value="">Todos os clientes</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-xs font-semibold text-slate-500">Status</label>
        <select
          value={status}
          onChange={(e) => onStatus(e.target.value as ActionsStatusFilter)}
          className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
        >
          {ACTION_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {ACTION_STATUS_LABEL[s]}
            </option>
          ))}
          <option value="all">Todos</option>
        </select>
      </div>

      <div>
        <label className="mb-1 block text-xs font-semibold text-slate-500">
          Data do relatório (semana)
        </label>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={week}
            onChange={(e) => onWeek(e.target.value)}
            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
          {week && (
            <button onClick={() => onWeek('')} className="text-xs text-slate-500 hover:underline">
              limpar
            </button>
          )}
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs font-semibold text-slate-500">
          Data do rever (semana)
        </label>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={reviewWeek}
            onChange={(e) => onReviewWeek(e.target.value)}
            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
          {reviewWeek && (
            <button
              onClick={() => onReviewWeek('')}
              className="text-xs text-slate-500 hover:underline"
            >
              limpar
            </button>
          )}
        </div>
      </div>

      {onReset && (
        <button
          type="button"
          onClick={onReset}
          disabled={isDefault}
          className={`ml-auto self-end rounded-md px-3 py-1.5 text-sm font-semibold transition ${
            isDefault
              ? 'cursor-not-allowed bg-slate-100 text-slate-400'
              : 'bg-red-600 text-white hover:bg-red-700'
          }`}
        >
          Limpar filtros
        </button>
      )}

      <p className="w-full text-[11px] text-slate-400">
        Cada data filtra pela <strong>semana de Segunda a Domingo</strong> que contém o dia escolhido.
        "Relatório" usa a data do relatório; "Rever" usa a data marcada no status "Rever em".
      </p>
    </div>
  )
}
